import { getYear, parseISO } from "date-fns";
import { useNavigate } from "react-router";
import { twJoin } from "tailwind-merge";

import { useImageResolver } from "@/api/hooks/useImageResolver";
import type { AssetBookingDto } from "@/api/types";
import { ContextMenu, type ContextMenuItem } from "@/components/ContextMenu/ContextMenu";
import { DeleteModal, useDeleteModal } from "@/components/DeleteModal/DeleteModal";
import { formatDate } from "@/components/FormattedDate/FormattedDate";
import { Edit05Icon } from "@/components/Icons/Edit05Icon";
import { MarkerPin01Icon } from "@/components/Icons/MarkerPin01Icon";
import { Trash02Icon } from "@/components/Icons/Trash02Icon";
import { Badge } from "@/design-system/Badge";
import { cardSurfaceVariants } from "@/design-system/Card";
import { timeWithoutSeconds } from "@/helpers/date";
import { twResolve } from "@/helpers/tw-resolve";
import { useSlug } from "@/hooks/useSlug";
import { bookableAssetMutations } from "@/queries/bookable-assets/mutations";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";
import { RuntimeBackgroundImage } from "@/ui-adapters/RuntimeBackgroundImage";

export function ReservationCard({
  reservation,
  isPast,
}: {
  reservation: AssetBookingDto;
  isPast?: boolean;
}): React.ReactNode {
  const slug = useSlug();
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();
  const resolveImage = useImageResolver();
  const { componentProps: deleteModalProps, openDeleteModal } = useDeleteModal<string>("delete-reservation-modal");

  const deleteReservationMutation = bookableAssetMutations.useDeleteBooking();

  const handleOpenReservation = () => {
    void navigate(
      routes.calendar.reservationDetails({
        slug,
        aid: reservation.asset.id,
        rid: reservation.id,
      }),
    );
  };

  const menuActions: ContextMenuItem[] = [
    {
      labels: {
        default: t("page.bookings.reservations.actions.edit"),
      },
      value: "edit",
      icon: <Edit05Icon />,
      "data-testid": "context-menu-edit-btn",
      callbackFn: () =>
        void navigate(
          routes.bookings.editBooking({
            slug,
            aid: reservation.asset.id,
            bid: reservation.id,
          }),
        ),
      isHidden: !reservation.canEdit || !!reservation.cancelledAt,
    },
    {
      labels: {
        default: t("page.bookings.reservations.actions.cancel"),
      },
      value: "cancel",
      icon: <Trash02Icon />,
      "data-testid": "context-menu-delete-btn",
      callbackFn: () => openDeleteModal(reservation.id),
      isHidden: !reservation.canDelete || !!reservation.cancelledAt,
    },
  ];
  const isCancelled = !!reservation.cancelledAt;
  const day = parseISO(reservation.date).getDate();
  const month = formatDate(i18n, "monthShort", parseISO(reservation.date));
  const year = getYear(parseISO(reservation.date));

  return (
    <>
      <article
        data-testid="reservation-card"
        role="link"
        tabIndex={0}
        aria-label={`Open your reservation for "${reservation.asset.name}" on ${reservation.startTime}`}
        className={twJoin(
          cardSurfaceVariants({ interactive: true }),
          twResolve("relative grid grid-cols-3 grid-rows-1 overflow-hidden", isPast && "opacity-70"),
        )}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            handleOpenReservation();
          }
        }}
        onClick={handleOpenReservation}
      >
        <section className="col-span-2 flex size-full items-center gap-space-sm p-card-compact">
          {/* Date */}
          <time className="flex flex-col items-center justify-center rounded-md bg-background-on-default px-space-sm py-space-md text-text-secondary">
            <span className="text-heading-2 leading-none">{day}</span>
            <span className="text-caption">{month}</span>
            <span className="text-caption">{year}</span>
          </time>
          {/* Info */}
          <section className="flex grow flex-col items-start justify-center gap-space-xs">
            <span className={twJoin("text-body-emphasis", isPast && "text-text-secondary")}>
              {reservation.asset.name}
            </span>
            <span className="text-caption text-text-secondary">{`${timeWithoutSeconds(reservation.startTime)} - ${timeWithoutSeconds(reservation.endTime)}`}</span>
            {reservation.asset.locationSpecification ? (
              <span className="flex items-center gap-space-xs text-text-secondary">
                <MarkerPin01Icon />
                <span className="line-clamp-1 text-caption">{reservation.asset.locationSpecification}</span>
              </span>
            ) : null}
          </section>
          <nav className="h-full">
            <ContextMenu items={menuActions} />
          </nav>
        </section>
        {/* Image */}
        <RuntimeBackgroundImage
          imageUrl={reservation.asset.images[0] ? resolveImage(reservation.asset.images[0], "intrinsic") : ""}
          presentation="cardAside"
        >
          {isCancelled && <Badge variant="danger">{t("page.bookings.reservations.reservation-cancelled")}</Badge>}
        </RuntimeBackgroundImage>
      </article>

      <DeleteModal
        title={t("page.bookings.reservation-details.delete.modal.title")}
        description={t("page.bookings.reservation-details.delete.modal.description")}
        onDelete={() =>
          deleteReservationMutation.mutateAsync({
            bookingId: reservation.id,
            assetId: reservation.asset.id,
          })
        }
        rejectBtnProps={{
          text: t("page.bookings.reservation-details.delete.modal.keep-reservation"),
          "data-testid": "modal-cancel-delete",
        }}
        deleteBtnProps={{
          text: t("page.bookings.reservation-details.delete.modal.cancel-reservation"),
          "data-testid": "modal-confirm-delete",
        }}
        {...deleteModalProps}
      />
    </>
  );
}
